import { deleteToken, getToken, isSupported } from 'firebase/messaging';
import {
  auth,
  firebaseConfig,
  getFirebaseMessaging,
  isFirebaseMessagingConfigured,
  resetFirebaseMessagingCache,
} from '../config/firebase';
import { buildWebDeviceInfo } from '../utils/webDeviceInfo';
import {
  getBrowserPushProfile,
  getWebPushEnvironmentCheck,
  hasWebPushCapabilities,
  isSecureWebPushContext,
  requiresPushUserGesture,
  SUPPORTED_PUSH_BROWSER_LABEL,
} from '../utils/webPushCapabilities';
import {
  registerNotificationDevice as defaultRegisterNotificationDevice,
  unregisterNotificationDevice as defaultUnregisterNotificationDevice,
} from './notifications';

let registerNotificationDevice = defaultRegisterNotificationDevice;
let unregisterNotificationDevice = defaultUnregisterNotificationDevice;

/** Route device register/unregister through React Query mutations when configured. */
export const configureFcmDeviceApi = ({
  registerDevice = defaultRegisterNotificationDevice,
  unregisterDevice = defaultUnregisterNotificationDevice,
} = {}) => {
  registerNotificationDevice = registerDevice;
  unregisterNotificationDevice = unregisterDevice;
};

export const resetFcmDeviceApi = () => {
  registerNotificationDevice = defaultRegisterNotificationDevice;
  unregisterNotificationDevice = defaultUnregisterNotificationDevice;
};

const FCM_TOKEN_STORAGE_KEY = 'ss_fcm_web_token';
const FCM_SESSION_REGISTERED_KEY = 'ss_fcm_registered_this_session';
export const NOTIFICATION_BANNER_DISMISSED_KEY = 'ss_notification_banner_dismissed';

export const isNotificationBannerDismissed = () => {
  try {
    return sessionStorage.getItem(NOTIFICATION_BANNER_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
};

export const dismissNotificationBanner = () => {
  try {
    sessionStorage.setItem(NOTIFICATION_BANNER_DISMISSED_KEY, '1');
  } catch {
    // ignore
  }
};
/**
 * Set on logout (localStorage) — portal won't auto-register push until user clicks Enable.
 * Browsers do not allow resetting Notification.permission from JS; site settings may still show Allow.
 */
export const FCM_PUSH_OPT_IN_REQUIRED_KEY = 'ss_fcm_push_opt_in_required';
const SW_PATH = '/firebase-messaging-sw.js';

export const FCM_BLOCK_REASON = {
  MISSING_VAPID: 'missing-vapid-key',
  INSECURE_CONTEXT: 'insecure-context',
  UNSUPPORTED_BROWSER: 'unsupported-browser',
  SAFARI_IOS_PWA_REQUIRED: 'safari-ios-pwa-required',
  PUSH_SERVICE_UNAVAILABLE: 'push-service-unavailable',
  PERMISSION_DENIED: 'permission-denied',
  TOKEN_FAILED: 'fcm-token-failed',
  USER_GESTURE_REQUIRED: 'user-gesture-required',
  PUSH_OPT_IN_REQUIRED: 'push-opt-in-required',
};

let registrationPromise = null;
let registrationInFlight = null;

const SAFARI_PUSH_SETUP_TIMEOUT_MS = 22000;
const DEFAULT_PUSH_SETUP_TIMEOUT_MS = 30000;

const getVapidKey = () => process.env.REACT_APP_FIREBASE_VAPID_KEY?.trim() || '';

const getPushSetupTimeoutMs = () =>
  requiresPushUserGesture() ? SAFARI_PUSH_SETUP_TIMEOUT_MS : DEFAULT_PUSH_SETUP_TIMEOUT_MS;

export const withFcmOperationTimeout = async (
  promise,
  ms = getPushSetupTimeoutMs(),
  label = 'Push setup',
) => {
  let timeoutId;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timeoutId = window.setTimeout(
          () => reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s`)),
          ms,
        );
      }),
    ]);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const getStoredFcmWebToken = () => {
  try {
    return localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
};

const setStoredFcmWebToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
    } else {
      localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
    }
  } catch {
    // ignore quota / private mode
  }
};

const markRegisteredThisSession = () => {
  try {
    sessionStorage.setItem(FCM_SESSION_REGISTERED_KEY, '1');
  } catch {
    // ignore
  }
};

const clearRegisteredThisSession = () => {
  try {
    sessionStorage.removeItem(FCM_SESSION_REGISTERED_KEY);
  } catch {
    // ignore
  }
};

const wasRegisteredThisSession = () => {
  try {
    return sessionStorage.getItem(FCM_SESSION_REGISTERED_KEY) === '1';
  } catch {
    return false;
  }
};

const isPushServiceError = (error) => {
  const message = error?.message || '';
  const code = error?.code || '';
  return (
    error?.name === 'AbortError' ||
    error?.name === 'NotAllowedError' ||
    /push service error/i.test(message) ||
    /registration failed/i.test(message) ||
    /user gesture/i.test(message) ||
    /not allowed/i.test(message) ||
    /messaging\/unsupported-browser/i.test(message) ||
    code === 'messaging/unsupported-browser'
  );
};

const isUserGesturePushError = (error) =>
  requiresPushUserGesture() &&
  (error?.name === 'NotAllowedError' || /user gesture/i.test(error?.message || ''));

const mapTokenErrorToBlockReason = (error) => {
  if (isUserGesturePushError(error)) {
    return FCM_BLOCK_REASON.USER_GESTURE_REQUIRED;
  }
  if (isPushServiceError(error)) {
    return FCM_BLOCK_REASON.PUSH_SERVICE_UNAVAILABLE;
  }
  const profile = getBrowserPushProfile();
  if (profile.isSupportedTarget) {
    return FCM_BLOCK_REASON.PUSH_SERVICE_UNAVAILABLE;
  }
  return FCM_BLOCK_REASON.TOKEN_FAILED;
};

const buildServiceWorkerUrl = () => {
  const query = new URLSearchParams({
    apiKey: firebaseConfig.apiKey || '',
    authDomain: firebaseConfig.authDomain || '',
    projectId: firebaseConfig.projectId || '',
    storageBucket: firebaseConfig.storageBucket || '',
    messagingSenderId: firebaseConfig.messagingSenderId || '',
    appId: firebaseConfig.appId || '',
  });
  return `${SW_PATH}?${query.toString()}`;
};

const waitForServiceWorkerActivation = (registration) =>
  new Promise((resolve, reject) => {
    if (registration.active) {
      resolve(registration);
      return;
    }

    const worker = registration.installing || registration.waiting;
    if (!worker) {
      resolve(registration);
      return;
    }

    const activationTimeoutMs = requiresPushUserGesture() ? 30000 : 15000;
    const timeoutId = window.setTimeout(() => {
      reject(new Error('Service worker activation timed out'));
    }, activationTimeoutMs);

    worker.addEventListener('statechange', () => {
      if (worker.state === 'activated') {
        window.clearTimeout(timeoutId);
        resolve(registration);
      }
      if (worker.state === 'redundant') {
        window.clearTimeout(timeoutId);
        reject(new Error('Service worker became redundant'));
      }
    });
  });

const isFirebaseMessagingServiceWorkerRegistration = (registration) => {
  const scriptUrl =
    registration.active?.scriptURL ||
    registration.installing?.scriptURL ||
    registration.waiting?.scriptURL ||
    '';
  const scope = registration.scope || '';
  return (
    scriptUrl.includes('firebase-messaging-sw.js') ||
    scope.includes('firebase-cloud-messaging-push-scope')
  );
};

const unregisterStaleMessagingWorkers = async () => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(
    registrations
      .filter(isFirebaseMessagingServiceWorkerRegistration)
      .map((registration) => registration.unregister()),
  );
};

export const registerMessagingServiceWorker = async ({ forceRefresh = false } = {}) => {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  if (forceRefresh) {
    registrationPromise = null;
    await unregisterStaleMessagingWorkers();
  }

  if (!registrationPromise) {
    registrationPromise = (async () => {
      const registration = await navigator.serviceWorker.register(buildServiceWorkerUrl());
      await waitForServiceWorkerActivation(registration);
      // Safari can hang indefinitely on serviceWorker.ready when a worker is already activating.
      if (registration.active) {
        return registration;
      }
      if (!requiresPushUserGesture()) {
        await navigator.serviceWorker.ready;
      }
      return registration;
    })().catch((error) => {
      registrationPromise = null;
      throw error;
    });
  }

  return registrationPromise;
};

/** Service worker that owns FCM push (must be used for showNotification). */
export const getMessagingServiceWorkerRegistration = async () => {
  if (!('serviceWorker' in navigator)) {
    return null;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  const messagingRegistration = registrations.find(isFirebaseMessagingServiceWorkerRegistration);

  if (messagingRegistration) {
    return messagingRegistration;
  }

  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
};

/** Register the messaging service worker without subscribing (safe on Safari before a click). */
export const warmUpFcmMessagingServiceWorker = async () => {
  try {
    await registerMessagingServiceWorker();
    return { success: true };
  } catch (error) {
    console.warn('[FCM] Service worker warm-up failed:', error);
    return { success: false, error };
  }
};

export const resetFcmRegistrationState = async () => {
  registrationPromise = null;
  resetFirebaseMessagingCache();
  await unregisterStaleMessagingWorkers();
};

const postTokenToBackend = async (token) => {
  const result = await registerNotificationDevice({
    token,
    deviceInfo: buildWebDeviceInfo(),
  });

  if (!result.success) {
    throw new Error(result.message);
  }

  setStoredFcmWebToken(token);
  markRegisteredThisSession();
};

export const getNotificationPermission = () => {
  if (typeof Notification === 'undefined') {
    return null;
  }
  return Notification.permission;
};

/** Call synchronously from a click handler — must not await anything before this. */
export const promptWebNotificationPermission = () => {
  if (typeof Notification === 'undefined') {
    return Promise.resolve('denied');
  }
  return Notification.requestPermission();
};

const mapEnvironmentReason = (reason) => {
  switch (reason) {
    case 'safari-ios-pwa-required':
      return FCM_BLOCK_REASON.SAFARI_IOS_PWA_REQUIRED;
    case 'insecure-context':
      return FCM_BLOCK_REASON.INSECURE_CONTEXT;
    case 'unsupported-browser':
      return FCM_BLOCK_REASON.UNSUPPORTED_BROWSER;
    default:
      return FCM_BLOCK_REASON.UNSUPPORTED_BROWSER;
  }
};

export const getWebPushAvailability = async () => {
  if (!isFirebaseMessagingConfigured()) {
    return { available: false, reason: FCM_BLOCK_REASON.MISSING_VAPID };
  }

  if (!getVapidKey()) {
    return { available: false, reason: FCM_BLOCK_REASON.MISSING_VAPID };
  }

  const environment = getWebPushEnvironmentCheck();
  const permission = typeof Notification !== 'undefined' ? Notification.permission : null;
  const browser = getBrowserPushProfile();

  if (!environment.ok) {
    return {
      available: false,
      reason: mapEnvironmentReason(environment.reason),
      permission,
      browser,
    };
  }

  const firebaseSupported = await isSupported();

  return {
    available: true,
    permission,
    firebaseSupported,
    browser,
  };
};

const obtainFcmTokenOnce = async ({ forceRefresh = false } = {}) => {
  const vapidKey = getVapidKey();
  const serviceWorkerRegistration = await registerMessagingServiceWorker({ forceRefresh });
  const messaging = await getFirebaseMessaging({ forceRefresh });

  if (!messaging) {
    return { token: null, blockReason: FCM_BLOCK_REASON.PUSH_SERVICE_UNAVAILABLE, error: null };
  }

  const token = await withFcmOperationTimeout(
    getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration,
    }),
    getPushSetupTimeoutMs(),
    'FCM token',
  );

  if (!token) {
    return { token: null, blockReason: FCM_BLOCK_REASON.PUSH_SERVICE_UNAVAILABLE, error: null };
  }

  return { token, blockReason: null, error: null };
};

const obtainFcmToken = async ({ forceRefresh = false, userGesture = false } = {}) => {
  if (Notification?.permission !== 'granted') {
    return { token: null, blockReason: FCM_BLOCK_REASON.PERMISSION_DENIED };
  }

  if (!getVapidKey()) {
    return { token: null, blockReason: FCM_BLOCK_REASON.MISSING_VAPID };
  }

  if (!isSecureWebPushContext()) {
    return { token: null, blockReason: FCM_BLOCK_REASON.INSECURE_CONTEXT };
  }

  if (!hasWebPushCapabilities()) {
    return { token: null, blockReason: FCM_BLOCK_REASON.UNSUPPORTED_BROWSER };
  }

  const environment = getWebPushEnvironmentCheck();
  if (!environment.ok) {
    return { token: null, blockReason: mapEnvironmentReason(environment.reason) };
  }

  if (requiresPushUserGesture() && !userGesture && !getStoredFcmWebToken()) {
    return { token: null, blockReason: FCM_BLOCK_REASON.USER_GESTURE_REQUIRED };
  }

  const skipPreReset = userGesture && requiresPushUserGesture();

  try {
    let result = await obtainFcmTokenOnce({ forceRefresh: skipPreReset ? false : forceRefresh });
    if (result.token) {
      return result;
    }

    if (skipPreReset || !forceRefresh) {
      if (requiresPushUserGesture() && !userGesture) {
        return { ...result, blockReason: FCM_BLOCK_REASON.USER_GESTURE_REQUIRED };
      }
      if (forceRefresh) {
        return result;
      }
    }

    await resetFcmRegistrationState();
    result = await obtainFcmTokenOnce({ forceRefresh: true });
    if (result.token) {
      return result;
    }

    if (requiresPushUserGesture() && !result.token) {
      return { ...result, blockReason: FCM_BLOCK_REASON.USER_GESTURE_REQUIRED };
    }

    return result;
  } catch (error) {
    console.error('[FCM] Failed to obtain token:', error?.code, error?.message, error);
    return { token: null, blockReason: mapTokenErrorToBlockReason(error), error };
  }
};

const runSetupFcmWebRegistration = async ({ forceRefresh = false, userGesture = false } = {}) => {
  if (isPushOptInRequired() && !userGesture) {
    return {
      success: false,
      skipped: true,
      permission: getNotificationPermission(),
      reason: FCM_BLOCK_REASON.PUSH_OPT_IN_REQUIRED,
    };
  }

  const { token, blockReason } = await obtainFcmToken({ forceRefresh, userGesture });
  if (!token) {
    return {
      success: false,
      skipped: true,
      permission: getNotificationPermission(),
      reason: blockReason,
    };
  }

  const stored = getStoredFcmWebToken();
  if (stored === token && wasRegisteredThisSession()) {
    return { success: true, token };
  }

  await postTokenToBackend(token);
  clearPushOptInRequired();
  return { success: true, token };
};

/**
 * Register FCM when permission is already granted (no browser prompt).
 * Browsers block Notification.requestPermission() unless triggered by a user gesture.
 */
export const setupFcmWebRegistration = async ({
  forceRefresh = false,
  userGesture = false,
} = {}) => {
  if (registrationInFlight) {
    return registrationInFlight;
  }

  registrationInFlight = (async () => {
    try {
      return await withFcmOperationTimeout(
        runSetupFcmWebRegistration({ forceRefresh, userGesture }),
        getPushSetupTimeoutMs(),
        'Push registration',
      );
    } catch (error) {
      console.error('[FCM] Web registration failed:', error);
      const timedOut = /timed out/i.test(error?.message || '');
      return {
        success: false,
        message: error?.message || 'FCM registration failed',
        reason: timedOut
          ? FCM_BLOCK_REASON.PUSH_SERVICE_UNAVAILABLE
          : mapTokenErrorToBlockReason(error),
      };
    } finally {
      registrationInFlight = null;
    }
  })();

  return registrationInFlight;
};

/**
 * Call from a click handler (not async-onClick with awaits before this).
 * Shows the browser permission dialog, then registers the FCM token.
 */
export const enableWebPushNotifications = async ({ forceRefresh = false } = {}) => {
  const permission = await promptWebNotificationPermission();
  if (permission !== 'granted') {
    return { success: false, permission, skipped: true };
  }
  clearPushOptInRequired();
  const fromGesture = true;
  if (forceRefresh && !requiresPushUserGesture()) {
    await resetFcmRegistrationState();
  }
  return setupFcmWebRegistration({
    forceRefresh: requiresPushUserGesture() ? false : forceRefresh,
    userGesture: fromGesture,
  });
};

/** Re-fetch token (e.g. on visibility) and re-register if it changed. */
export const refreshFcmWebRegistrationIfNeeded = async () => {
  if (!getVapidKey() || Notification.permission !== 'granted' || isPushOptInRequired()) {
    return;
  }

  try {
    const { token } = await obtainFcmToken();
    if (!token) return;

    const stored = getStoredFcmWebToken();
    if (token !== stored || !wasRegisteredThisSession()) {
      await postTokenToBackend(token);
    }
  } catch (error) {
    console.warn('[FCM] Token refresh check failed:', error);
  }
};

const clearLocalFcmState = () => {
  setStoredFcmWebToken(null);
  clearRegisteredThisSession();
  registrationPromise = null;
};

export const clearNotificationBannerDismissed = () => {
  try {
    sessionStorage.removeItem(NOTIFICATION_BANNER_DISMISSED_KEY);
  } catch {
    // ignore
  }
};

export const markPushOptInRequired = () => {
  try {
    localStorage.setItem(FCM_PUSH_OPT_IN_REQUIRED_KEY, '1');
  } catch {
    // ignore
  }
};

export const clearPushOptInRequired = () => {
  try {
    localStorage.removeItem(FCM_PUSH_OPT_IN_REQUIRED_KEY);
  } catch {
    // ignore
  }
};

export const isPushOptInRequired = () => {
  try {
    return localStorage.getItem(FCM_PUSH_OPT_IN_REQUIRED_KEY) === '1';
  } catch {
    return false;
  }
};

export const getBrowserNotificationRevokeHint = () => {
  const { isChrome, isEdge, isMacSafari } = getBrowserPushProfile();
  if (isMacSafari) {
    return 'Safari → Settings for This Website → Notifications → Deny.';
  }
  if (isEdge) {
    return 'Edge → lock icon in the address bar → Permissions → Notifications → Block.';
  }
  if (isChrome) {
    return 'Chrome → lock icon in the address bar → Site settings → Notifications → Block.';
  }
  return 'Open your browser’s site settings for this page and set Notifications to Block.';
};

/** Ends all browser push subscriptions (Notification.permission may still show as granted). */
const unsubscribeWebPushSubscriptions = async () => {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map(async (registration) => {
        const subscription = await registration.pushManager?.getSubscription?.();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }),
    );
  } catch (error) {
    console.warn('[FCM] Push subscription unsubscribe failed:', error);
  }
};

const deleteFcmTokenFromFirebase = async () => {
  if (!isFirebaseMessagingConfigured()) {
    return;
  }

  try {
    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      return;
    }

    let registration = null;
    try {
      registration = await registerMessagingServiceWorker();
    } catch {
      registration = await getMessagingServiceWorkerRegistration();
    }

    if (registration) {
      await deleteToken(messaging, { serviceWorkerRegistration: registration });
      return;
    }

    await deleteToken(messaging);
  } catch (error) {
    console.warn('[FCM] deleteToken failed:', error);
  }
};

/**
 * Logout / sign-out cleanup: unregister on backend (while auth is active), revoke push locally.
 * Browsers do not allow resetting Notification.permission from JS — user must change site settings.
 */
export const teardownFcmWebRegistration = async ({ skipApi = false } = {}) => {
  const token = getStoredFcmWebToken();

  let result = { success: true };
  if (!skipApi && token && auth.currentUser) {
    result = await unregisterNotificationDevice({ token });
    if (!result.success) {
      console.warn('[FCM] Unregister failed:', result.message);
    }
  }

  await unsubscribeWebPushSubscriptions();
  await deleteFcmTokenFromFirebase();
  clearLocalFcmState();
  clearNotificationBannerDismissed();
  markPushOptInRequired();
  resetFirebaseMessagingCache();
  registrationPromise = null;
  await unregisterStaleMessagingWorkers();

  return result;
};

/** Unregister when the user turns off site notifications (permission no longer granted). */
export const handleWebPushPermissionRevoked = () => teardownFcmWebRegistration();

/**
 * Listen for notification permission changes and unregister when push is turned off.
 * Returns a cleanup function.
 */
export const startNotificationPermissionWatcher = (onRevoked) => {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return () => {};
  }

  let lastPermission = Notification.permission;
  let permissionStatus;

  const handlePermissionChange = () => {
    const current = Notification.permission;
    if (lastPermission === 'granted' && current !== 'granted') {
      onRevoked(current);
    }
    lastPermission = current;
  };

  if (navigator.permissions?.query) {
    navigator.permissions
      .query({ name: 'notifications' })
      .then((status) => {
        permissionStatus = status;
        permissionStatus.onchange = handlePermissionChange;
      })
      .catch(() => undefined);
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      handlePermissionChange();
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    if (permissionStatus) {
      permissionStatus.onchange = null;
    }
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
};

const getBrowserDisplayName = () => {
  const { isChrome, isEdge, isFirefox, isOpera, isBrave, isMacSafari } = getBrowserPushProfile();
  if (isEdge) return 'Microsoft Edge';
  if (isOpera) return 'Opera';
  if (isBrave) return 'Brave';
  if (isFirefox) return 'Firefox';
  if (isChrome) return 'Google Chrome';
  if (isMacSafari) return 'Safari';
  return null;
};

export const getFcmBlockReasonMessage = (reason) => {
  const browserName = getBrowserDisplayName();

  switch (reason) {
    case FCM_BLOCK_REASON.MISSING_VAPID:
      return isFirebaseMessagingConfigured()
        ? 'Push is not configured. Set REACT_APP_FIREBASE_VAPID_KEY in .env and restart the dev server.'
        : 'Firebase is not configured. Set REACT_APP_FIREBASE_PROJECT_ID (and related REACT_APP_FIREBASE_* keys) in .env, then restart npm start.';
    case FCM_BLOCK_REASON.INSECURE_CONTEXT:
      return `Push requires HTTPS or localhost. ${SUPPORTED_PUSH_BROWSER_LABEL} are supported when the connection is secure.`;
    case FCM_BLOCK_REASON.PERMISSION_DENIED:
      return 'Notifications are blocked for this site. Allow them in your browser settings, then reload and click Retry.';
    case FCM_BLOCK_REASON.USER_GESTURE_REQUIRED:
      return browserName === 'Safari'
        ? 'Safari: click Enable or Retry once to connect push notifications (Safari only allows this during a button click).'
        : 'Click Enable or Retry to connect push notifications.';
    case FCM_BLOCK_REASON.PUSH_OPT_IN_REQUIRED:
      return (
        'Push was disconnected when you signed out. Click Enable alerts to turn notifications on again. ' +
        `Your browser may still show this site as Allow — to block notifications entirely: ${getBrowserNotificationRevokeHint()}`
      );
    case FCM_BLOCK_REASON.SAFARI_IOS_PWA_REQUIRED:
      return (
        'Safari on iPhone/iPad: add this app to the Home Screen (Share → Add to Home Screen), open it from the icon, ' +
        `then enable notifications. Desktop ${SUPPORTED_PUSH_BROWSER_LABEL} are also supported.`
      );
    case FCM_BLOCK_REASON.PUSH_SERVICE_UNAVAILABLE: {
      const prefix = browserName
        ? `${browserName}: notifications are allowed, but push registration failed or timed out.`
        : 'Notifications are allowed, but push registration failed or timed out.';
      const safariHint =
        browserName === 'Safari'
          ? ' Click Enable alerts again (do not reload first). Allow any Safari permission prompt. If it still fails, confirm Firebase has an APNs key for web push and disable content blockers for this site.'
          : '';
      const firefoxHint =
        browserName === 'Firefox'
          ? ' In Firefox, avoid Private Browsing, allow notifications for this site, and disable strict tracking protection for this origin if push still fails.'
          : '';
      const braveHint =
        browserName === 'Brave'
          ? ' In Brave, allow notifications and ensure Shields are not blocking scripts from gstatic.com (Firebase service worker).'
          : '';
      return (
        `${prefix} Use ${SUPPORTED_PUSH_BROWSER_LABEL}, allow notifications for this site, ` +
        'open over HTTPS or localhost, disable ad-blockers for this page, then click Retry.' +
        safariHint +
        firefoxHint +
        braveHint
      );
    }
    case FCM_BLOCK_REASON.UNSUPPORTED_BROWSER:
      return `Web push is supported in ${SUPPORTED_PUSH_BROWSER_LABEL}. Please switch to one of those browsers.`;
    case FCM_BLOCK_REASON.TOKEN_FAILED:
      return getFcmBlockReasonMessage(FCM_BLOCK_REASON.PUSH_SERVICE_UNAVAILABLE);
    default:
      return getFcmBlockReasonMessage(FCM_BLOCK_REASON.UNSUPPORTED_BROWSER);
  }
};
